import type { TravelInsuranceQuotesData } from '@hyphengroup/cms-components/types/travelInsurance';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  type PageDataContextValue,
  usePageDataContext,
} from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { travelCompareQuotesParamsHash } from '$/(cms)/_modules/travelQuotesService/helpers';
import { TravelCompareQuotesParams } from '$/(cms)/_modules/travelQuotesService/types';

const DEBUG_LOGS_ENABLED = false;
const POLLING_INTERVAL_MS = 1_500;

type TravelSearchPollArgs = {
  queryParamsReady: boolean;
  travelQueryParams: TravelCompareQuotesParams;
};

/**
 * Polls for travel quotes based on the provided query parameters, into the page's quote slice.
 */
export function useTravelSearchPoll(args: TravelSearchPollArgs) {
  const {
    pageData: { travelQuotes },
    setTravelQuotes,
  } = usePageDataContext();
  useTravelQuotesPoll({ ...args, travelQuotes, setTravelQuotes });
}

/**
 * The poll itself, over whatever slice its caller keeps: a route with no CMS page of its own holds
 * the quote in its own state.
 */
export function useTravelQuotesPoll({
  queryParamsReady,
  travelQueryParams,
  travelQuotes,
  setTravelQuotes,
}: TravelSearchPollArgs & {
  travelQuotes: TravelInsuranceQuotesData | undefined;
  setTravelQuotes: PageDataContextValue['setTravelQuotes'];
}) {
  const travelQueryParamsHashRef = useRef(
    travelCompareQuotesParamsHash(travelQueryParams),
  );
  const travelQueryParamsHash = useMemo(() => {
    const hash = travelCompareQuotesParamsHash(travelQueryParams);
    travelQueryParamsHashRef.current = hash;
    return hash;
  }, [travelQueryParams]);

  const requestsInProgress = useRef<Array<string>>([]);

  const requestQuotes = useCallback(
    async (newParams: TravelCompareQuotesParams) => {
      debugLog('requestQuotes:start', newParams);
      const newParamsHash = travelCompareQuotesParamsHash(newParams);

      let newQuotes: Awaited<
        ReturnType<typeof feApiFrontClient.travelCompareQuotes>
      >;
      try {
        newQuotes = await feApiFrontClient.travelCompareQuotes(newParams);
      } catch (error) {
        // A refused request (a trip the route rejects, a network error) is a finished one: the
        // trip is released, and settles as failed so the page stops waiting for a price.
        debugLog('requestQuotes:threw', newParamsHash, error);
        requestsInProgress.current = requestsInProgress.current.filter(
          (hash) => hash !== newParamsHash,
        );
        if (travelQueryParamsHashRef.current === newParamsHash) {
          setTravelQuotes({ params: newParams, status: 'fail', premiums: [] });
        }
        return;
      }
      // new params are in progress
      if (travelQueryParamsHashRef.current !== newParamsHash) {
        debugLog(
          'requestQuotes:stale-response',
          newParamsHash,
          travelQueryParamsHashRef.current,
        );
        requestsInProgress.current = requestsInProgress.current.filter(
          (hash) => hash !== newParamsHash,
        );
        return;
      }

      setTravelQuotes({
        params: newParams,
        status: newQuotes.status,
        premiums: newQuotes.data,
      });
      debugLog('requestQuotes:response', newParamsHash, newQuotes.status);
      // finished
      if (newQuotes.status !== 'inprogress') {
        requestsInProgress.current = requestsInProgress.current.filter(
          (hash) => hash !== newParamsHash,
        );
        debugLog('requestQuotes:done', newParamsHash);
        return;
      }

      // still in progress
      debugLog('requestQuotes:polling-next', newParamsHash);
      await delay(POLLING_INTERVAL_MS);
      await requestQuotes(newParams);
    },
    [setTravelQuotes],
  );

  const startRequestQuotesProcess = useCallback(async () => {
    // already in progress
    if (requestsInProgress.current.includes(travelQueryParamsHash)) {
      debugLog(
        'startRequestQuotesProcess:skip-existing',
        travelQueryParamsHash,
      );
      return;
    }
    // start calc
    requestsInProgress.current.push(travelQueryParamsHash);
    debugLog('startRequestQuotesProcess:start', travelQueryParamsHash);

    setTravelQuotes({
      params: travelQueryParams,
      status: 'inprogress',
      premiums: [],
      oldPremiumsIds: travelQuotes?.premiums?.map(
        (premium) => premium?.brokerProductCode || '',
      ),
    });
    await requestQuotes(travelQueryParams);
  }, [
    requestQuotes,
    setTravelQuotes,
    travelQueryParams,
    travelQueryParamsHash,
    travelQuotes?.premiums,
  ]);

  useEffect(() => {
    (async () => {
      const lastReqParamsHash = travelCompareQuotesParamsHash(
        travelQuotes?.params ?? {},
      );
      // need to recalculate
      if (lastReqParamsHash !== travelQueryParamsHash && queryParamsReady) {
        debugLog(
          'useEffect:params-changed',
          lastReqParamsHash,
          travelQueryParamsHash,
        );
        await startRequestQuotesProcess();
      }
    })();
  }, [
    queryParamsReady,
    requestQuotes,
    setTravelQuotes,
    startRequestQuotesProcess,
    travelQueryParams,
    travelQueryParamsHash,
    travelQuotes,
  ]);

  // SSR backend req was not successful, need to re-run on the client
  useEffect(() => {
    if (
      travelQuotes?.status !== 'success' &&
      travelQuotes?.isBackendData &&
      queryParamsReady
    ) {
      // Re-run the request on the client
      debugLog('useEffect:ssr-retry');
      startRequestQuotesProcess();
    }
  }, [
    queryParamsReady,
    startRequestQuotesProcess,
    travelQuotes?.isBackendData,
    travelQuotes?.status,
  ]);
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function debugLog(...args: Array<unknown>) {
  if (!DEBUG_LOGS_ENABLED) {
    return;
  }
  console.debug('[TravelPolicySearch]', ...args);
}
